import Cookies from "js-cookie";
import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { marketingHook } from "../overrides/hooks/init";
import { decodeJwt } from "jose";
import { IToken } from "@/components/Authentication/types/auth";
import { useCallback } from "react";

export const useCookies = () => {
  const settings = useSettingsContext();

  const hasAcceptedCookies = useCallback(() => {
    return !!Cookies.get("rcl_cookies_accepted");
  }, []);

  const acceptCookies = useCallback(() => {
    Cookies.set("rcl_cookies_accepted", "true");
  }, []);

  const acceptConsentCookies = useCallback(() => {
    Cookies.set("rcl_consent_given", "true");
  }, []);

  const acceptPreferencesCookies = useCallback(() => {
    Cookies.set("rcl_preferences_consent", "true");
  }, []);

  const acceptMarketingCookies = useCallback(() => {
    Cookies.set("rcl_marketing_consent", "true");
  }, []);

  const statisticsConsentGiven = useCallback(() => {
    return Cookies.get("rcl_statistics_consent");
  }, []);

  const marketingConsentGiven = useCallback(() => {
    return Cookies.get("rcl_marketing_consent");
  }, []);

  const preferenceConsentGiven = useCallback(() => {
    return Cookies.get("rcl_preferences_consent");
  }, []);

  const acceptStatisticCookies = useCallback(() => {
    if (!settings?.analytics?.key) return;
    ReactGA.initialize(settings.analytics.key);
    Cookies.set("rcl_statistics_consent", "true");
  }, [settings]);

  const acceptAllCookies = useCallback(() => {
    if (settings?.analytics?.key) {
      ReactGA.initialize(settings.analytics.key);
    }
    Cookies.set("rcl_cookies_accepted", "true");
    Cookies.set("rcl_marketing_consent", "true");
    Cookies.set("rcl_consent_given", "true");
    Cookies.set("rcl_preferences_consent", "true");
    Cookies.set("rcl_statistics_consent", "true");
  }, [settings]);

  const deleteStatisticCookies = useCallback(() => {
    Cookies.remove("_ga");
    Cookies.remove("_gat");
    Cookies.remove("_gid");
    if (settings?.analytics?.key) {
      const gaDisableKey = `ga-disable-${settings.analytics.key}`;
      Object.assign(window, { [gaDisableKey]: true });
    }
  }, [settings]);

  const getCustomUserID = useCallback(() => {
    const token = Cookies.get("token");
    if (token) {
      try {
        const tokenVals = decodeJwt<IToken>(token);
        if (settings?.analytics?.userField && settings?.analytics?.userField in tokenVals) {
          return tokenVals[settings.analytics.userField as keyof IToken];
        } else {
          return tokenVals["partner_id"];
        }
      } catch {
        return undefined;
      }
    }
    return undefined;
  }, [settings]);

  const updateStatisticScripts = useCallback(() => {
    const statisticsConsent = Cookies.get("rcl_statistics_consent");
    if (
      (settings?.features?.analytics ?? false) &&
      (settings?.analytics?.key ?? false) &&
      statisticsConsent
    ) {
      if (settings!.features?.analyticsCustomUserID ?? false) {
        const customUserID = getCustomUserID();
        if (customUserID) {
          ReactGA.set({ userId: customUserID });
        }
      }
      ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
    }
  }, [settings, getCustomUserID]);

  const updateMarketingScripts = useCallback(() => {
    if (Cookies.get("rcl_marketing_consent")) {
      marketingHook();
    }
  }, []);

  const getToken = useCallback(() => {
    return Cookies.get("token");
  }, []);

  const setToken = useCallback((token: string) => {
    Cookies.set("token", token, { secure: true, sameSite: "Lax" });
  }, []);

  const removeToken = useCallback(() => {
    Cookies.remove("token");
  }, []);

  const getOpenIdToken = useCallback(() => {
    return Cookies.get("openid_token");
  }, []);

  const setOpenIdToken = useCallback((token: string) => {
    Cookies.set("openid_token", token, { secure: true, sameSite: "Lax" });
  }, []);

  const removeOpenIdToken = useCallback(() => {
    Cookies.remove("openid_token");
  }, []);

  const getOpenIdNonce = useCallback(() => {
    return Cookies.get("openid_nonce");
  }, []);

  const setOpenIdNonce = useCallback((nonce: string) => {
    Cookies.set("openid_nonce", nonce, { secure: true, sameSite: "Lax" });
  }, []);

  const removeOpenIdNonce = useCallback(() => {
    Cookies.remove("openid_nonce");
  }, []);

  const getOpenIdKey = useCallback(() => {
    return Cookies.get("openid_key");
  }, []);

  const setOpenIdKey = useCallback((key: string) => {
    Cookies.set("openid_key", key, { secure: true, sameSite: "Lax" });
  }, []);

  const removeOpenIdKey = useCallback(() => {
    Cookies.remove("openid_key");
  }, []);

  return {
    hasAcceptedCookies,
    acceptCookies,
    acceptConsentCookies,
    acceptPreferencesCookies,
    acceptMarketingCookies,
    statisticsConsentGiven,
    marketingConsentGiven,
    preferenceConsentGiven,
    acceptStatisticCookies,
    acceptAllCookies,
    deleteStatisticCookies,
    updateStatisticScripts,
    updateMarketingScripts,
    getToken,
    setToken,
    removeToken,
    getOpenIdToken,
    setOpenIdToken,
    removeOpenIdToken,
    getOpenIdNonce,
    setOpenIdNonce,
    removeOpenIdNonce,
    getOpenIdKey,
    setOpenIdKey,
    removeOpenIdKey,
  };
};
